/**
 * Shared dev warning definitions and getter for article-style stories.
 * Centralises the node shape, checks, and getDevWarnings so stories don't duplicate.
 */

/** Minimal node shape needed for warning checks (blocks, optional lede/media for a11y, authors). Accepts readonly (e.g. GraphQL) shapes. */
export interface DevWarningsNode {
  /** Author names from the resource; used for unassigned-author check. */
  authors?: ReadonlyArray<{ readonly name?: string | null }> | null;
  blocks?: ReadonlyArray<{
    readonly __typename?: string;
    /** Block-level alt (CMS "Alternative Text" override). */
    readonly alt?: string | null;
    readonly image?: { readonly alt?: string | null } | null;
    readonly images?: ReadonlyArray<{
      readonly image?: { readonly alt?: string | null } | null;
    }> | null;
  }> | null;
  /** Present when resource has lede media (e.g. LedeMediaImageType); used for blank-alt check. */
  ledeMedia?: {
    readonly __typename?: string;
    readonly alt?: string | null;
    readonly image?: { readonly title?: string | null } | null;
  } | null;
}

/** Credit entry shape used by stories (e.g. config/credits). */
export type CreditsEntry = { title: string; names: string };

export interface DevWarningDefinition {
  id: string;
  message: string;
  /** Return true when this warning should be shown. */
  check: (node: DevWarningsNode) => boolean;
}

export interface DevWarningsOptions {
  /**
   * Whether this story renders a credits section backed by config/credits.
   * Disable this when credits are intentionally omitted from the story.
   */
  creditsSectionEnabled?: boolean;
  /**
   * Positive when the story generates in-article ad slots from paragraph runs.
   * In that case the raw WordPress block list is not expected to contain ad HTML.
   */
  generatedAdParagraphRunLength?: number | null;
}

/** True when a credit item has names missing or still placeholder. */
function isNamesNotSet(names: string): boolean {
  const t = names.trim();
  return t === "" || t === "—";
}

/** True when a string is missing or blank (for alt/title checks). */
function isBlank(s: string | null | undefined): boolean {
  return (s ?? "").trim() === "";
}

/** True when an author name looks unassigned: contains a period and is all lowercase (e.g. "j."). */
function looksLikeUnassignedAuthor(name: string | null | undefined): boolean {
  const t = (name ?? "").trim();
  if (!t) {
    return false;
  }
  return t.includes(".") && t === t.toLowerCase();
}

/**
 * Returns a getDevWarnings function that uses the given credits for credit-related checks.
 * Use this so each story passes its own config/credits; the rest of the checks are shared.
 */
export function createGetDevWarnings(
  credits: ReadonlyArray<CreditsEntry>,
  options: DevWarningsOptions = {},
): (node: DevWarningsNode) => string[] {
  const creditsSectionEnabled = options.creditsSectionEnabled ?? true;
  const hasGeneratedAdSlots = (options.generatedAdParagraphRunLength ?? 0) > 0;

  const definitions: DevWarningDefinition[] = [
    {
      id: "lede-image-no-alt",
      message: "Accessibility: Lede image has no alt text.",
      check: (node) => {
        const lede = node.ledeMedia;
        if (lede?.__typename !== "LedeMediaImageType" || !lede.image) {
          return false;
        }
        return isBlank(lede.alt);
      },
    },
    {
      id: "block-images-no-alt",
      message:
        "Accessibility: One or more block or gallery images have no alt text.",
      check: (node) => {
        const blocks = node.blocks ?? [];
        return blocks.some((b) => {
          if (b.__typename === "CoreImageBlockType") {
            if (b.image === null || b.image === undefined) {
              return false;
            }
            const alt = b.alt?.trim() || b.image.alt?.trim();
            return isBlank(alt);
          }
          if (b.__typename === "CoreGalleryBlockType") {
            return (b.images ?? []).some((item) => {
              if (!item.image) {
                return false;
              }
              return isBlank(item.image.alt);
            });
          }
          return false;
        });
      },
    },
    {
      id: "no-ad-slots",
      message:
        "Ads: No ad slots in the article body. Add before publish unless intentional.",
      check: (node) => {
        if (hasGeneratedAdSlots) {
          return false;
        }
        const hasAdSlots =
          node.blocks?.some((b) => b.__typename === "CoreHTMLBlockType") ??
          false;
        return !hasAdSlots;
      },
    },
    {
      id: "credits-none",
      message: "Credits: No credits configured. Add entries in config/credits.",
      check: () => creditsSectionEnabled && credits.length === 0,
    },
    {
      id: "credits-names-not-set",
      message:
        "Credits: One or more credit entries have names not set. Set names in config/credits.",
      check: () => {
        if (!creditsSectionEnabled) {
          return false;
        }
        if (credits.length === 0) {
          return false;
        }
        return credits.some((item) => isNamesNotSet(item.names));
      },
    },
    {
      id: "author-not-assigned",
      message:
        "Credits: Author name has a period and is all lowercase; author may not be assigned properly.",
      check: (node) => {
        const authors = node.authors ?? [];
        return authors.some((a) => looksLikeUnassignedAuthor(a.name));
      },
    },
  ];

  return (node: DevWarningsNode): string[] =>
    definitions.filter((def) => def.check(node)).map((def) => def.message);
}
